import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, ToggleLeft, ToggleRight, Settings, Key, ChevronDown, Save, X } from 'lucide-react';
import {
  fetchCountryPaymentGateways,
  toggleCountryGateway,
  saveGatewayCredentials,
  fetchGatewayCredentials,
  fetchCountriesForDropdown,
  updateCountryGatewayConfig,
} from '../services/api.ts';
import './PaymentGateways.css';

interface CountryOption { id: number; name: string; code: string; }

interface GatewayRow {
  gateway_id: number;
  code: string;
  name: string;
  gateway_type: string;
  icon_url: string | null;
  country_gateway_id: number | null;
  is_active: boolean;
  currency: string;
  display_order: number;
  config: Record<string, any>;
}

// Available Stripe payment method types (excludes 'link' intentionally)
const STRIPE_PAYMENT_METHODS: { value: string; label: string }[] = [
  { value: 'card', label: 'Credit / Debit Card' },
  { value: 'klarna', label: 'Klarna (Buy Now Pay Later)' },
  { value: 'afterpay_clearpay', label: 'Afterpay / Clearpay' },
  { value: 'affirm', label: 'Affirm' },
  { value: 'au_becs_debit', label: 'BECS Direct Debit (AU)' },
  { value: 'ideal', label: 'iDEAL' },
  { value: 'sepa_debit', label: 'SEPA Direct Debit' },
  { value: 'bancontact', label: 'Bancontact' },
  { value: 'eps', label: 'EPS' },
  { value: 'giropay', label: 'Giropay' },
  { value: 'p24', label: 'Przelewy24' },
  { value: 'sofort', label: 'Sofort' },
];

// Credential fields expected per gateway
const GATEWAY_CREDENTIAL_FIELDS: Record<string, string[]> = {
  stripe: ['publishable_key', 'secret_key', 'webhook_secret'],
  square: ['application_id', 'access_token', 'location_id', 'webhook_signature_key', 'webhook_notification_url'],
  eway: ['api_key', 'api_password'],
};

const PaymentGateways: React.FC = () => {
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [gateways, setGateways] = useState<GatewayRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // Credentials modal
  const [credModal, setCredModal] = useState<{ gateway: GatewayRow; environment: string } | null>(null);
  const [credFields, setCredFields] = useState<Record<string, string>>({});
  const [existingCreds, setExistingCreds] = useState<any[]>([]);
  const [savingCreds, setSavingCreds] = useState(false);
  const [credError, setCredError] = useState<string | null>(null);

  // Payment methods config (Stripe)
  const [pmConfigGateway, setPmConfigGateway] = useState<GatewayRow | null>(null);
  const [pmSelected, setPmSelected] = useState<string[]>([]);
  const [savingPmConfig, setSavingPmConfig] = useState(false);
  const [pmConfigError, setPmConfigError] = useState<string | null>(null);

  // Load countries
  useEffect(() => {
    fetchCountriesForDropdown()
      .then((data) => {
        setCountries(data);
        if (data.length > 0) setSelectedCountry(data[0].id);
      })
      .catch(console.error);
  }, []);

  // Load gateways whenever country changes
  const loadGateways = useCallback(async () => {
    if (!selectedCountry) return;
    try {
      setLoading(true);
      setError(null);
      const data = await fetchCountryPaymentGateways(selectedCountry);
      setGateways(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load gateways');
    } finally {
      setLoading(false);
    }
  }, [selectedCountry]);

  useEffect(() => { loadGateways(); }, [loadGateways]);

  // Toggle gateway active
  const handleToggle = async (gw: GatewayRow) => {
    if (!selectedCountry) return;
    setTogglingId(gw.gateway_id);
    try {
      await toggleCountryGateway(selectedCountry, gw.gateway_id, !gw.is_active);
      await loadGateways();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toggle failed');
    } finally {
      setTogglingId(null);
    }
  };

  // Open credentials modal
  const openCredentials = async (gw: GatewayRow) => {
    if (!selectedCountry) return;
    setCredModal({ gateway: gw, environment: 'test' });
    setCredFields({});
    setCredError(null);
    setExistingCreds([]);
    try {
      const creds = await fetchGatewayCredentials(selectedCountry, gw.gateway_id);
      setExistingCreds(creds);
      // Pre-populate form with first credential set (prefer test environment)
      const initial = creds.find((c: any) => c.environment === 'test') || creds[0];
      if (initial) {
        setCredModal({ gateway: gw, environment: initial.environment });
        setCredFields(initial.credentials || {});
      }
    } catch {
      setExistingCreds([]);
    }
  };

  // Save credentials
  const handleSaveCreds = async () => {
    if (!credModal || !selectedCountry) return;
    setSavingCreds(true);
    setCredError(null);
    try {
      await saveGatewayCredentials(
        selectedCountry,
        credModal.gateway.gateway_id,
        { environment: credModal.environment, credentials: credFields },
      );
      setCredModal(null);
      await loadGateways();
    } catch (err) {
      setCredError(err instanceof Error ? err.message : 'Failed to save credentials');
    } finally {
      setSavingCreds(false);
    }
  };

  // Open payment methods config modal (Stripe only)
  const openPmConfig = (gw: GatewayRow) => {
    setPmConfigGateway(gw);
    const current = gw.config?.payment_method_types || ['card'];
    setPmSelected([...current]);
    setPmConfigError(null);
  };

  // Toggle a payment method checkbox
  const togglePm = (method: string) => {
    setPmSelected((prev) =>
      prev.includes(method) ? prev.filter((m) => m !== method) : [...prev, method],
    );
  };

  // Save payment methods config
  const handleSavePmConfig = async () => {
    if (!pmConfigGateway || !selectedCountry) return;
    if (pmSelected.length === 0) {
      setPmConfigError('Select at least one payment method.');
      return;
    }
    setSavingPmConfig(true);
    setPmConfigError(null);
    try {
      await updateCountryGatewayConfig(selectedCountry, pmConfigGateway.gateway_id, {
        config: { ...pmConfigGateway.config, payment_method_types: pmSelected },
      });
      setPmConfigGateway(null);
      await loadGateways();
    } catch (err) {
      setPmConfigError(err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setSavingPmConfig(false);
    }
  };

  const gatewayTypeLabel = (type: string) => {
    switch (type) {
      case 'card': return 'Credit/Debit Card';
      case 'wallet': return 'Digital Wallet';
      case 'bnpl': return 'Buy Now Pay Later';
      default: return type;
    }
  };

  return (
    <div className="payment-gateways-page">
      <div className="page-header">
        <div>
          <h1><CreditCard size={24} /> Payment Gateways</h1>
          <p className="page-subtitle">Configure payment gateways per country</p>
        </div>
      </div>

      {/* Country selector */}
      <div className="country-selector">
        <label>Country:</label>
        <select
          value={selectedCountry || ''}
          onChange={(e) => setSelectedCountry(Number(e.target.value))}
        >
          {countries.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="loading-state">Loading gateways...</div>
      ) : (
        <div className="gateways-grid">
          {gateways.map((gw) => (
            <div key={gw.gateway_id} className={`gateway-card ${gw.is_active ? 'active' : 'inactive'}`}>
              <div className="gateway-card-header">
                <div className="gateway-info">
                  <h3>{gw.name}</h3>
                  <span className="gateway-type-badge">{gatewayTypeLabel(gw.gateway_type)}</span>
                </div>
                <button
                  className={`toggle-btn ${gw.is_active ? 'on' : 'off'}`}
                  onClick={() => handleToggle(gw)}
                  disabled={togglingId === gw.gateway_id}
                  title={gw.is_active ? 'Deactivate' : 'Activate'}
                >
                  {gw.is_active ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                </button>
              </div>

              <div className="gateway-card-details">
                <div className="detail-row">
                  <span className="detail-label">Code:</span>
                  <span className="detail-value"><code>{gw.code}</code></span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Currency:</span>
                  <span className="detail-value">{gw.currency}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className={`status-badge ${gw.is_active ? 'active' : 'inactive'}`}>
                    {gw.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="gateway-card-actions">
                <button className="btn-configure" onClick={() => openCredentials(gw)}>
                  <Key size={16} /> Configure Credentials
                </button>
                {gw.code === 'stripe' && (
                  <button className="btn-configure" onClick={() => openPmConfig(gw)}>
                    <Settings size={16} /> Payment Methods
                  </button>
                )}
              </div>
            </div>
          ))}

          {gateways.length === 0 && (
            <div className="empty-state">
              No payment gateways found. Gateway seed data may not have been applied.
            </div>
          )}
        </div>
      )}

      {/* Credentials Modal */}
      {credModal && (
        <div className="modal-overlay" onClick={() => setCredModal(null)}>
          <div className="modal-content credentials-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Key size={20} /> Configure {credModal.gateway.name}</h2>
              <button className="close-btn" onClick={() => setCredModal(null)}><X size={20} /></button>
            </div>

            <div className="modal-body">
              {/* Existing credentials */}
              {existingCreds.length > 0 && (
                <div className="existing-creds">
                  <h4>Existing Credentials</h4>
                  {existingCreds.map((c) => (
                    <div key={c.id} className="cred-row">
                      <span className={`env-badge ${c.environment}`}>{c.environment.toUpperCase()}</span>
                      <span className="cred-keys">{c.credential_keys.join(', ')}</span>
                      <span className={`cred-status ${c.is_active ? 'active' : ''}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="cred-form">
                <h4>Set Credentials</h4>

                <div className="form-group">
                  <label>Environment</label>
                  <select
                    value={credModal.environment}
                    onChange={(e) => {
                      const env = e.target.value;
                      setCredModal({ ...credModal, environment: env });
                      const match = existingCreds.find((c: any) => c.environment === env);
                      setCredFields(match ? match.credentials || {} : {});
                    }}
                  >
                    <option value="test">Test / Sandbox</option>
                    <option value="live">Live / Production</option>
                  </select>
                </div>

                {(GATEWAY_CREDENTIAL_FIELDS[credModal.gateway.code] || []).map((field) => (
                  <div className="form-group" key={field}>
                    <label>{field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</label>
                    <input
                      type="text"
                      placeholder={`Enter ${field}`}
                      value={credFields[field] || ''}
                      onChange={(e) => setCredFields({ ...credFields, [field]: e.target.value })}
                      autoComplete="off"
                    />
                  </div>
                ))}

                {credError && <div className="form-error">{credError}</div>}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setCredModal(null)}>Cancel</button>
              <button className="btn-save" onClick={handleSaveCreds} disabled={savingCreds}>
                <Save size={16} /> {savingCreds ? 'Saving...' : 'Save Credentials'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Methods Config Modal (Stripe) */}
      {pmConfigGateway && (
        <div className="modal-overlay" onClick={() => setPmConfigGateway(null)}>
          <div className="modal-content credentials-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Settings size={20} /> Payment Methods — {pmConfigGateway.name}</h2>
              <button className="close-btn" onClick={() => setPmConfigGateway(null)}><X size={20} /></button>
            </div>

            <div className="modal-body">
              <p className="pm-config-hint">
                Select the payment methods customers can use at checkout. At least one must be enabled.
              </p>
              <div className="pm-checkbox-list">
                {STRIPE_PAYMENT_METHODS.map((pm) => (
                  <label key={pm.value} className="pm-checkbox-item">
                    <input
                      type="checkbox"
                      checked={pmSelected.includes(pm.value)}
                      onChange={() => togglePm(pm.value)}
                    />
                    <span className="pm-checkbox-label">{pm.label}</span>
                    <code className="pm-checkbox-code">{pm.value}</code>
                  </label>
                ))}
              </div>
              {pmConfigError && <div className="form-error">{pmConfigError}</div>}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setPmConfigGateway(null)}>Cancel</button>
              <button className="btn-save" onClick={handleSavePmConfig} disabled={savingPmConfig}>
                <Save size={16} /> {savingPmConfig ? 'Saving...' : 'Save Payment Methods'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentGateways;
